import { presentationSpeechTurns } from "./tts-presentation-turns";
import {
  normalizedSpeechText,
  plainSpeechText,
  remainingTextAfterPrefix,
} from "./tts-reply-text";
import { withoutHeardSentences } from "./tts-spoken-overlap";

/** What an execution's speech has really played, sentence by sentence.
 *  `narrated` is the part guide narration said: only that is paraphrased by
 *  the reply, so only that is matched loosely. The reply's own sentences
 *  share its topic words without repeating each other. */
type ReplySpeechHistory = {
  updatedAt: number;
  segments: string[];
  narrated: string[];
};

export const REPLY_DEDUP_MS = 30_000;
const replySpeechHistories = new Map<string, ReplySpeechHistory>();

function recentReplyHistory(executionId: string): ReplySpeechHistory | null {
  const history = replySpeechHistories.get(executionId);
  if (!history) return null;
  if (
    presentationSpeechTurns.lastCompletedAt(executionId) !== undefined ||
    Date.now() - history.updatedAt <= REPLY_DEDUP_MS
  )
    return history;
  replySpeechHistories.delete(executionId);
  return null;
}

export function markReplySpoken(
  executionId: string,
  text: string,
  source: "reply" | "narration" = "reply",
): void {
  const plain = plainSpeechText(text);
  if (!plain) return;
  const history = recentReplyHistory(executionId) ?? {
    updatedAt: 0,
    segments: [],
    narrated: [],
  };
  const normalized = normalizedSpeechText(plain);
  if (
    normalized &&
    !history.segments.some(
      (segment) => normalizedSpeechText(segment) === normalized,
    )
  ) {
    history.segments.push(plain);
  }
  if (source === "narration" && !history.narrated.includes(plain)) {
    history.narrated.push(plain);
  }
  history.updatedAt = Date.now();
  replySpeechHistories.set(executionId, history);
  if (replySpeechHistories.size > 256) {
    replySpeechHistories.delete(replySpeechHistories.keys().next().value!);
  }
}

export function hasRecentReplySpeak(
  executionId: string,
  text: string,
): boolean {
  const history = recentReplyHistory(executionId);
  if (!history) return false;
  const normalized = normalizedSpeechText(text);
  if (!normalized) return false;
  const combined = normalizedSpeechText(history.segments.join(" "));
  if (combined === normalized) return true;
  return history.segments.some(
    (segment) => normalizedSpeechText(segment) === normalized,
  );
}

export function replyTailAfterSpokenHistory(
  executionId: string,
  text: string,
): { type: "duplicate" } | { type: "tail"; text: string } | null {
  const history = recentReplyHistory(executionId);
  if (!history || history.segments.length === 0) return null;
  if (hasRecentReplySpeak(executionId, text)) return { type: "duplicate" };
  const fullPlain = plainSpeechText(text);
  for (let index = 0; index < history.segments.length; index += 1) {
    const spokenPlain = history.segments.slice(index).join(" ").trim();
    if (normalizedSpeechText(spokenPlain) === normalizedSpeechText(text)) {
      return { type: "duplicate" };
    }
    const tail = remainingTextAfterPrefix(text, spokenPlain);
    if (tail && tail !== fullPlain) return { type: "tail", text: tail };
  }
  return null;
}

/** `text` without sentences that repeat speech this execution already played. */
export function unspokenReplySentences(
  executionId: string,
  text: string,
): string {
  const history = recentReplyHistory(executionId);
  return history
    ? withoutHeardSentences(text, {
        spoken: history.segments,
        narrated: history.narrated,
      })
    : text;
}

export function forgetReplyHistory(executionId: string): void {
  replySpeechHistories.delete(executionId);
}

export function clearReplyHistories(): void {
  replySpeechHistories.clear();
}
